import { Action } from '@reduxjs/toolkit';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Dispatch } from 'redux';

import { Error } from '../components/error/error';
import { VedleggError } from '../components/error/vedleggError';
import {
    BesvartSporsmalRad,
    OppsummeringTabell,
    saveButtonProps,
    ViewBesvartSporsmal,
} from '../components/oppsummering/oppsummering';
import { OppsummeringFooter } from '../components/oppsummering/oppsummering-footer';
import { StepNavigation } from '../components/step-navigation/step-navigation';
import { useBankregnr } from '../config';
import { getVedlegg, resetSavingState, VedleggSource } from '../meldskade/reducer/skademelding-actions';
import { AppState } from '../reducers';
import { SavingState } from '../reducers/savingState';
import { urler } from '../router/router-urler';
import { toUploadedVedleggDTO, VedleggIdInternal } from '../sporsmalsflyt/filopplasting/filopplasting-types';
import { useWindowScrollTop } from '../sporsmalsflyt/hooks';
import { isKontaktinfoSporsmal } from '../sporsmalsflyt/kontaktinfo/kontaktinfo-types';
import { besvarteSporsmalToSvarDTO } from '../sporsmalsflyt/reducer/current-steg';
import { previousStepAction } from '../sporsmalsflyt/reducer/sporsmalsflyt-actions';
import { IdentifikatorType } from '../sporsmalsflyt/reducer/sporsmalsflyt-types';
import { KlageDTO, SporsmalType } from '../types/generated-types';
import { SideType } from '../utils/piwik-utils';
import {
    KlageResult,
    KlageStatusDTO,
    saveKlage,
    saveKlageFailure,
    saveKlageSuccess,
    saveKlageVedleggFailure,
    sendKlageApi,
} from './reducer/klage-actions';

export const KlageOppsummering = () => {
    const bankregnr = useBankregnr();
    const state = useSelector((state: AppState) => state);
    const {
        savingState,
        sporsmalsflytState: { besvarteSporsmal },
    } = state.klage;
    const windowScrollTop = useWindowScrollTop();

    const pakrevdBesvarteTekstSporsmal = besvarteSporsmal.filter(
        (besvartSporsmal) =>
            besvartSporsmal.type !== SporsmalType.Filopplasting && besvartSporsmal.type !== SporsmalType.Kontaktinfo,
    );
    const kontaktinfo = besvarteSporsmal.find(isKontaktinfoSporsmal);

    const dispatch: Dispatch<Action> = useDispatch();

    const navigate = useNavigate();

    const vedlegg = getVedlegg(state, VedleggSource.Klage);

    useEffect(() => {
        if (savingState === SavingState.Success) {
            dispatch(resetSavingState());
            navigate(urler().klageKvitteringUrl);
        }
    }, [savingState, dispatch, navigate]);

    const viewVedlegg = (file: VedleggIdInternal) => (
        <tr key={file.id}>
            <td className="oppsummering-table-svar">{file.fileName}</td>
        </tr>
    );

    const sendKlage = (bankregnr: string) => {
        const vedlegg = getVedlegg(state, VedleggSource.Klage);

        const klage: KlageDTO = {
            svar: besvarteSporsmalToSvarDTO(state.klage.sporsmalsflytState.besvarteSporsmal),
            vedlegg: toUploadedVedleggDTO(vedlegg),
        };

        dispatch(saveKlage());

        sendKlageApi(klage, bankregnr).then((data: KlageResult) => {
            if (
                data.status in
                [
                    KlageStatusDTO.FailedUploadingVedlegg,
                    KlageStatusDTO.FailedUnexpectedVedleggException,
                    KlageStatusDTO.FailedVedleggDoesNotExist,
                ]
            ) {
                dispatch(saveKlageVedleggFailure());
            } else if (data.status === KlageStatusDTO.Ok) {
                dispatch(saveKlageSuccess());
            } else {
                dispatch(saveKlageFailure());
            }
        });
    };

    const submit = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();
        sendKlage(bankregnr);
    };

    if (savingState === SavingState.Success) {
        return null;
    }

    return (
        <div className="container__standard-width oppsummering">
            <p className="oppsummering-title">Se over informasjonen du har fylt inn før du sender klagesaken</p>

            <OppsummeringTabell header="Om klagen">
                {pakrevdBesvarteTekstSporsmal.map((besvartSporsmal) => (
                    <ViewBesvartSporsmal besvartSporsmal={besvartSporsmal} key={besvartSporsmal.sporsmal.id} />
                ))}
            </OppsummeringTabell>

            <OppsummeringTabell header="Dokumenter">{vedlegg.vedleggIds.map(viewVedlegg)}</OppsummeringTabell>

            {kontaktinfo && (
                <OppsummeringTabell header="Kontaktinfo">
                    <BesvartSporsmalRad sporsmal="Fullt navn" svar={kontaktinfo.navn} />
                    <BesvartSporsmalRad sporsmal="Mobilnummer" svar={kontaktinfo.mobilnummer} />
                    <BesvartSporsmalRad sporsmal="E-post" svar={kontaktinfo.epost} />
                    {kontaktinfo.identifikatorType === IdentifikatorType.Organisasjonsnummer && (
                        <BesvartSporsmalRad sporsmal="Organisasjonsnummer" svar={kontaktinfo.organisasjonsnummer} />
                    )}
                </OppsummeringTabell>
            )}

            <OppsummeringFooter />
            {savingState === SavingState.UgyldigVedlegg && <VedleggError />}
            {savingState === SavingState.Failure && (
                <Error
                    headerText="Her gikk noe galt – prøv igjen!"
                    bodyText="Meldingen ble ikke sendt inn, prøv igjen. Dersom problemet gjentar seg flere ganger, kan du ringe oss på tlf 915&nbsp;03&nbsp;850."
                />
            )}

            <StepNavigation
                bankregnr={bankregnr}
                prev={{
                    onClick: () => {
                        windowScrollTop();
                        dispatch(previousStepAction());
                    },
                }}
                next={saveButtonProps(savingState, submit)}
                isOppsummering
                sideDetail={{ type: SideType.Oppsummeringsside, skademeldingstype: 'klage' }}
            />
        </div>
    );
};
